export const buildFolderPdf = async (doc, folder, cardsPerPage, renderContentForPdf) => {
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const layoutConfig = {
        6: { rows: 3, cols: 2, fontSize: 10, imgSize: 30, fontFactor: 3.5 },
        8: { rows: 4, cols: 2, fontSize: 9, imgSize: 25, fontFactor: 3.8 },
        10: { rows: 5, cols: 2, fontSize: 8, imgSize: 20, fontFactor: 4.0 },
    };
    const config = layoutConfig[cardsPerPage];
    const margin = 15;
    const cardW = (pageW - margin * (config.cols + 1)) / config.cols;
    const cardH = (pageH - 40 - config.rows * margin) / config.rows;
    const cards = folder.cards;

    const drawHeader = () => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(30);
        doc.setTextColor(139, 92, 246);
        doc.text('FLASHFONIC', pageW / 2, 20, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(16);
        doc.setTextColor(31, 41, 55);
        doc.text('Listen. Flash it. Learn.', pageW / 2, 30, { align: 'center' });
    };

    const getImageAsBase64 = async (url) => {
        try {
            const response = await fetch(url);
            if (!response.ok) return null;
            const blob = await response.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        } catch {
            return null;
        }
    };

    const drawCardContent = async (content, prefix, x, y, w, h) => {
        doc.setFontSize(config.fontSize);
        doc.setTextColor(0, 0, 0);

        const isChemical = (typeof content === 'object' && content !== null) || (typeof content === 'string' && content.includes('CHEM['));
        if (!isChemical) {
            const fullText = `${prefix} ${renderContentForPdf(content)}`;
            const textLines = doc.splitTextToSize(fullText, w - 10);
            const textHeight = textLines.length * (config.fontSize / config.fontFactor);
            const textY = y + h / 2 - textHeight / 2;
            doc.text(textLines, x + w / 2, textY, { align: 'center' });
            return;
        }

        let structure = { reactants: [], products: [], reagents: [] };
        if (content && content.type === 'full_reaction') {
            structure = content;
        } else if (Array.isArray(content)) {
            structure.products = content;
        }

        let imgSize = config.imgSize;
        let plusSignWidth = 5;
        let arrowZoneWidth = 20;
        let totalContentWidth = structure.reactants.length * imgSize + Math.max(0, structure.reactants.length - 1) * plusSignWidth + (structure.reactants.length > 0 ? arrowZoneWidth : 0) + structure.products.length * imgSize + Math.max(0, structure.products.length - 1) * plusSignWidth;
        if (totalContentWidth > w - 10) {
            const scaleFactor = (w - 10) / totalContentWidth;
            imgSize *= scaleFactor;
            plusSignWidth *= scaleFactor;
            arrowZoneWidth *= scaleFactor;
        }
        totalContentWidth = structure.reactants.length * imgSize + Math.max(0, structure.reactants.length - 1) * plusSignWidth + (structure.reactants.length > 0 ? arrowZoneWidth : 0) + structure.products.length * imgSize + Math.max(0, structure.products.length - 1) * plusSignWidth;

        let currentX = x + (w - totalContentWidth) / 2;
        const contentY = y + (h - imgSize) / 2;
        doc.text(prefix, x + 5, y + 7);

        for (let i = 0; i < structure.reactants.length; i++) {
            const match = structure.reactants[i].match(/CHEM\[(.*?)\]/);
            if (match && match[1]) {
                const url = `https://cactus.nci.nih.gov/chemical/structure/${encodeURIComponent(match[1])}/image?format=jpg`;
                const imgData = await getImageAsBase64(url);
                if (imgData) doc.addImage(imgData, 'JPEG', currentX, contentY, imgSize, imgSize);
            }
            currentX += imgSize;
            if (i < structure.reactants.length - 1) {
                doc.text('+', currentX + plusSignWidth / 2, y + h / 2, { align: 'center', baseline: 'middle' });
                currentX += plusSignWidth;
            }
        }
        if (structure.reactants.length > 0) {
            const arrowX = currentX + arrowZoneWidth / 2;
            doc.setFontSize(config.fontSize * 0.8);
            doc.text(structure.reagents.join(', '), arrowX, y + h / 2 - 5, { align: 'center' });
            doc.setFontSize(config.fontSize * 1.5);
            doc.text('-->', arrowX, y + h / 2 + 2, { align: 'center', baseline: 'middle' });
            currentX += arrowZoneWidth;
        }
        for (let i = 0; i < structure.products.length; i++) {
            const match = structure.products[i].match(/CHEM\[(.*?)\]/);
            if (match && match[1]) {
                const url = `https://cactus.nci.nih.gov/chemical/structure/${encodeURIComponent(match[1])}/image?format=jpg`;
                const imgData = await getImageAsBase64(url);
                if (imgData) doc.addImage(imgData, 'JPEG', currentX, contentY, imgSize, imgSize);
            }
            currentX += imgSize;
            if (i < structure.products.length - 1) {
                doc.text('+', currentX + plusSignWidth / 2, y + h / 2, { align: 'center', baseline: 'middle' });
                currentX += plusSignWidth;
            }
        }
    };

    for (const side of ['front', 'back']) {
        if (side === 'back' && cards.length > 0) doc.addPage();
        drawHeader();
        for (let i = 0; i < cards.length; i++) {
            const pageIndex = Math.floor(i / cardsPerPage);
            if (pageIndex > 0 && i % cardsPerPage === 0) {
                doc.addPage();
                drawHeader();
            }
            const card = cards[i];
            const cardIndexOnPage = i % cardsPerPage;
            const row = Math.floor(cardIndexOnPage / config.cols);
            const col = cardIndexOnPage % config.cols;
            const cardX = margin + col * (cardW + margin);
            const cardY = 40 + row * (cardH + margin);
            doc.setDrawColor(0);
            doc.rect(cardX, cardY, cardW, cardH);
            const contentToDraw = side === 'front' ? card.question : card.answer;
            const prefix = side === 'front' ? 'Q:' : 'A:';
            await drawCardContent(contentToDraw, prefix, cardX, cardY, cardW, cardH);
        }
    }
};


